import { RJSFSchema, UiSchema } from "@rjsf/utils";

interface MailboxGroup {
  uid: string;
  name: string;
  description?: string;
  vanityLink: string;
  mailboxes: MailboxDetails[];
}

interface PublicProfile {
  uid: string;
  name: string;
  email: string;
  hasAvatar: Boolean;
  fingerprint: [
    {
      name: string;
      codePoint: number;
      title: string;
    }
  ];
  locale: string;
  timeZone: string;
}

interface SkpEnvironment {
  server_version: number;
  storagehostIdx: number;
  product_name: string;
  product_url: URL;
  provider_name: string;
  provider_url: URL;
  data_privacy_url_de: URL;
  data_privacy_url_en: URL;
  system_status_url: URL;
  available_lang: string[];
  offered_clients: string[];
  motd: string;
  show_remote_access: boolean;
  password_autocomplete: boolean;
  self_register_promo: boolean;
  freemiumEnabled: boolean;
  locale: string;
  mypublic_enabled: boolean;
  privacyModeEnabled: boolean;
  keepMeLoggedInChecked: boolean;
  minimumPasswordLength: number;
  extendedPasswordCriteriaEnabled: boolean;
  transferPasswordRequired: boolean;
  ttlEnabled: boolean;
  priorityEnabled: boolean;
  recipientAuthenticationEnabled: boolean;
  inviteEnabled: boolean;
  portalUrl: URL;
  timeZone: string;
  customerName: string;
  expiration: Expiration;
  privacyStatementUrl?: URL;
  legalNoticeUrl?: URL;
  browserLanguage: string;
  self_register_enabled: boolean;
  self_register_url?: URL;
  passwordRecoveryEnabled: boolean;
  passwordRecoveryMessage: string;
  lang: string;
}

interface OrganizationEnvironment {
  baseUrlId: URL;
  baseUrlSkp: URL;
  baseUrlDrive: URL;
  availableLocales: string[];
  onPremises: boolean;
  productName: string;
  productUrl: URL;
  providerName: string;
  providerUrl: URL;
  supportContact: string;
  organizationProfile: OrganizationProfile;
  oidcSettings: OIDCSettings;
  limitations: Limitations;
}

interface OrganizationProfile {
  name: string;
  addressLine1: string;
  addressLine2: string;
  postCode: string;
  city: string;
  state: string;
  country: string;
  phoneNumber: string;
  locale: string;
  timeZone: string;
  globalSignature: string;
  legalNoticeUrl: URL;
  privacyPolicyUrl: URL;
  uid: string;
  hostname: string;
  inactiveHostnames: string[];
  assets: string[];
  mfaRequired: boolean;
}

interface OIDCSettings {
  redirectUrl: URL;
  clients: OIDCClient[];
}

interface OIDCClient {
  type: string;
  issuer: URL;
  name: string;
  clientId: string;
}

interface Limitations {
  orgUid: string;
  serviceEnabled: boolean;
  users: number;
  transferVolumeGb: number;
  storageVolumeGb: number;
  gdprContract: boolean;
  secureSpaces: boolean;
  newTransferLimit: number;
  newTransferDrainRate: number;
  newTransferReceiversLimit: number;
  adminPortal: boolean;
  mailboxGroups: number;
  changeHostname: string;
}

interface Expiration {
  values: number[];
  default: number;
}

interface BaseAnon {
  email: string;
  uid?: string;
}

interface TransferDraftAnon extends BaseAnon {
  name: string;
}

interface TransferAnon extends BaseAnon {
  realname: string;
}

interface TransferReceiver extends TransferAnon {
  portalUrl?: URL;
  type?: string;
  clientType?: string;
}

interface Group {
  idx: number;
  name: string;
}

interface Folder {
  idx: number;
  name: string;
}

/// Files that have been added to the transfer draft
type TransferDraftFile = [SkpFile, File];

interface TransferDraft {
  description: string;
  subject: string;
  receivers?: TransferReceiver[];
  files: TransferDraftFile[];
  anon: TransferDraftAnon;
  errors: Set<TransferDraftErrors>;
  acknowledgement: boolean;
  ttl?: number;
  priority?: number;
  protectionType: ProtectionType;
  password?: string;
  repeatPassword?: string;
  prefilled: boolean;
  dynamicFormData?: Record<string, unknown> | null;
  // This is used to not clear prefilled mailbox fields once
  // transfer is sent
  prefillKeys?: {
    static: Array<"name" | "email" | "subject" | "description">;
    dynamic: string[];
  } | null;
}

interface MimeRejection {
  fileName: string;
  rejectedFileId: string;
  reservationUid: string;
  reservationToken: string;
  remainingFiles: TransferDraftFile[];
  completedBytes: number;
  totalBytes: number;
}

enum ProtectionType {
  none = "none",
  password = "password",
  recipientAuthentication = "recipientAuthentication",
}

enum TransferDraftErrors {
  blankSenderName,
  invalidSenderEmail,
  invalidFileCount,
  blankPassword,
  wrongMinimumPasswordLength,
  disobeysExtendedPasswordRequirements,
  blankRepeatPassword,
  invalidRepeatPassword,
}

interface Transfer {
  uid: string;
  type: TransferType;
  protection: TransferProtection;
  senderUid: string | null;
  senderAddress: string;
  senderName: string;
  recipientUid: string;
  recipientAddress: string;
  recipientName: string;
  destinationType: TransferDestinationType;
  priority: number;
  anonAddress: string;
  anonName: string;
  subject: string;
  message: string;
  signature: string | null;
  uploadedAt: Date;
  accessedAt: Date | null;
  lastDownloadAt: Date | null;
  expiresAt: Date;
  malwareStatus: MalwareStatus;
  bounceReason: string | null;
  locked: boolean;
  blocked: boolean;
  totalSize: number;
  objects: TransferFileObject[];
}

interface PasswordProtectedTransfer {
  uid: string;
  type: TransferType;
  protection: TransferProtection;
  senderUid: string | null;
  recipientUid: string;
  recipientAddress: string;
  destinationType: TransferDestinationType;
  passwordAttempts: number;
  locked: boolean;
  blocked: boolean;
}

interface TransferFileObject {
  uid: string;
  name: string;
  contentLength: number;
  contentType: string;
  malwareStatus: MalwareStatus;
  malwareText: string;
  hashsum: string;
  url: URL;
  downloadCounter: number;
  quarantined: boolean;
}

enum TransferType {
  p2p = "p2p",
  drop = "drop",
  mailbox = "mailbox",
  archive = "archive",
}

enum MalwareStatus {
  ok = "ok",
  unknown = "unknown",
  error = "error",
  infected = "infected",
}

enum TransferProcessingState {
  newTransfer,
  malwareScan,
  hashCalculation,
  notification,
  completed,
}
interface SkpFile {
  name: string;
  size: number;
  id: string;
}

interface SkpTransferFile extends SkpFile {
  objectId: string;
  url: URL;
  hashsum: string;
  malware: SkpMalware;
  downloadCounter: number;
  mimeType: string;
  quarantined: boolean;
}

interface SkpMalware {
  status: MalwareStatus;
  text: string;
}

interface ApiException {
  error: {
    code: number;
    message: string;
    details: string[];
    // Record containing specific errors for structured fields
    validation?: Record<string, unknown> | null;
  };
}

interface ErrorMessageBudle {
  domain: string;
  locale_data: LocaleData;
}

interface LocaleData {
  messages: Record<string, string[]>;
}

export interface FieldSchema extends RJSFSchema {
  // Extra keys from backend if needed
  [key: string]: unknown;
}

interface MailboxDetails {
  uid: string;
  secure: boolean;
  name?: string;
  email: string;
  vanityLink?: string;
  slogan: string | null;
  absent: boolean;
  absenceMessage: string | null;
  fieldsSchema?: FieldSchema | null;
  uiSchema?: UiSchema | null;
  mimeTypeFilterEnabled: boolean;
  mimeTypeFilterDescription: string | null;
  maxFiles: number;
  maxFileSizeMb: number;
  volumeLimitExceeded: boolean;
}

interface CreateMailboxReservationRequest {
  subject?: string;
  description?: string;
  anonSender: string;
  anonEmail: string;
}

enum TransferProtection {
  none = "none",
  password = "password",
  recipientAuthentication = "recipientAuthentication",
}
interface ReservationCreationResponse {
  uid: string;
  anonSender: string;
  anonEmail: string;
  recipients: TransferRecipient[];
  objects: ReservationObject[];
  deliveryNotification: boolean;
  ttl: number;
  priority: number;
  protection: TransferProtection;
}

interface UpdateReservationRequest {
  uid: string;
  subject: string;
  description: string;
  anonSender: string;
  anonEmail?: string;
  recipients?: TransferReceipt[];
  folder?: number;
  objects: ReservationObject[];
  deliveryNotification?: boolean;
  ttl?: number;
  priority?: number;
  protection?: TransferProtection;
  password?: string;
  fields?: Record<string, unknown>;
}

interface UpdateReservationResponse {
  uid: string;
  subject?: string;
  description?: string;
  anonSender?: string;
  anonEmail?: string;
  recipients: TransferReceipt[];
  folder?: number;
  objects: ReservationObject[];
  deliveryNotification?: boolean;
  ttl: number;
  priority: number;
  protection: TransferProtection;
  password?: string;
}

interface MailboxReservation {
  subject?: string;
  description?: string;
  deliveryNotification?: boolean;
  ttl?: number;
  priority?: number;
  protection?: string;
  password?: string;
  uid: string;
  anonSender: string;
  anonEmail: string;
  recipients: TransferRecipient[];
  objects: ReservationObject[];
}

interface TransferRecipient {
  uid?: string;
  name?: string;
  email: string;
  destinationType: TransferDestinationType;
}

enum TransferDestinationType {
  to = "to",
  cc = "cc",
  bcc = "bcc",
}

interface ReservationObject {
  clientId: string;
  name: string;
  size: number;
  intendedSize: number;
}

interface TransferReceipt {
  recipientId: string;
  type: "p2p" | "archive" | "mailbox";
}

interface FieldsPrefillData {
  type: FormPrefillType;
  mailboxUid?: string;
  name?: string;
  email?: string;
  subject?: string;
  description?: string;
  immutable?: string[];
}

enum FormPrefillType {
  // prefill mailbox fields (name and email) when receiver is replying to sender
  replyPrefill = "replyPrefill",
  // prefill mailbox fields when mailbox url contains encoded prefill-data
  mailboxPrefill = "mailboxPrefill",
}

// ==========================================================================
//                               Drive share-links
// ==========================================================================

/**
 * A single file shared through a drive share-link.
 *
 * Mirrors the `Metadata` DTO returned by
 * `GET /api/drive/v3/get/{shareLinkUid}` (guest access).
 *
 * Fields marked optional here are optional in the spec
 * (only `path`, `mimeType`, `uid` and `size` are guaranteed).
 */
interface ShareLinkFile {
  uid: string;
  path: string;
  size: number;
  mimeType: string;
  intendedSize?: number;
  createdAt?: Date | string;
  modifiedAt?: Date | string;
  accessedAt?: Date | string;
  deletedAt?: Date | string | null;
  etag?: string;
  blurHash?: string;
  aspectRatio?: number;
  shareLinkUids?: string[];
}

/**
 * The resolved payload of `GET /api/drive/v3/get/{shareLinkUid}`.
 *
 * The `token` is a short-lived guest JWT. The same value is also set as an
 * HttpOnly cookie scoped to `/api/drive/v3/get/{shareLinkUid}` by the server,
 * so file downloads work without attaching an `Authorization` header.
 */
interface ShareLinkResolution {
  token: string;
  spaceName: string;
  spaceDescription?: string;
  spaceHasAvatar: boolean;
  /** UID of the user who created the share-link (skalio-id). */
  createdBy: string;
  /**
   * When the share-link expires, rendered in the header as
   * "Gültig bis DD.MM.YYYY". ISO string or Date.
   */
  expiresAt?: Date | string;
  files: ShareLinkFile[];
}

/**
 * Normalized payload for the public (unauthenticated) viewer of a *published
 * space*, assembled by {@link resolvePublishedSpace}: the space's display
 * metadata and its full, flat file list.
 *
 * Unlike a share-link (a flat set of files), a published space is a directory
 * tree — but the API returns the files flat, each carrying its full POSIX
 * `path`. The folder hierarchy is derived client-side from those paths (see
 * `useSpaceTree`), so no per-folder round-trips are needed.
 *
 * The guest token is also set as an HttpOnly cookie (via `?cookie=true`) scoped
 * to the space, so file download/preview requests authenticate automatically.
 */
interface PublishedSpaceResolution {
  spaceName: string;
  /** Who published the space (the owning org's name) — "Bereitgestellt von …". */
  provider?: string;
  /** Total number of files in the space (directory entries excluded). */
  fileCount: number;
  /** When the space was last updated — "Aktualisiert am DD.MM.YYYY". ISO string. */
  modifiedAt?: string;
  /** Blurhash of the space avatar; placeholder while the image loads. */
  blurhash?: string | null;
  files: ShareLinkFile[];
}

/**
 * An open space invitation. The server never echoes the invitee address back,
 * since the link carries no secret.
 */
interface SpaceInvitationDetails {
  /** UID of the inviting user (skalio-id); name via the public profile. */
  inviterUid: string;
  /** Optional message from the inviter; null when none was written. */
  personalNote: string | null;
  /** Access right the invitation grants on the space. */
  privilege: "read" | "write" | "admin" | "guest";
  space: {
    uid: string;
    name: string;
    description: string | null;
    orgUid: string;
    secure: boolean;
    /** How many people already collaborate in the space. */
    collaboratorCount?: number;
    /** Bytes stored in the space. */
    usedVolume?: number | null;
    /** Blurhash of the space avatar; placeholder while the image loads. */
    blurhash?: string | null;
  };
}

/**
 * Where an e-mail address can authenticate. The target host must come from
 * here, never from the host the page runs on.
 */
interface AuthNextResolution {
  nextStep: "login" | "signup" | "abort";
  /** Host to authenticate on; null iff `nextStep` is "abort". */
  nextHop: { fqdn: string; uid: string } | null;
  /** OIDC provider; null iff `nextStep` is "abort". */
  provider: string | null;
}

export type {
  ApiException,
  CreateMailboxReservationRequest,
  ReservationCreationResponse,
  UpdateReservationRequest,
  UpdateReservationResponse,
  SkpEnvironment,
  OrganizationEnvironment,
  ErrorMessageBudle,
  Expiration,
  Folder,
  Group,
  LocaleData,
  MailboxDetails,
  MailboxReservation,
  PublicProfile,
  ReservationObject,
  SkpFile,
  TransferAnon,
  TransferDraft,
  TransferDraftAnon,
  TransferDraftFile,
  TransferReceipt,
  TransferReceiver,
  TransferRecipient,
  MailboxGroup,
  MimeRejection,
  FieldsPrefillData,
  SkpTransferFile,
  Transfer,
  PasswordProtectedTransfer,
  TransferFileObject,
  ShareLinkFile,
  ShareLinkResolution,
  PublishedSpaceResolution,
  SpaceInvitationDetails,
  AuthNextResolution,
};

export {
  MalwareStatus,
  TransferProtection,
  ProtectionType,
  TransferDraftErrors,
  TransferProcessingState,
  FormPrefillType,
  TransferType,
  TransferDestinationType,
};
